import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, readJsonResponse } from '../api/client';
import RisksTopBar from '../components/RisksTopBar';

const MOSCOW = [55.751244, 37.618423];

function loadYmaps(apiKey) {
  if (window.ymaps) {
    return window.ymaps.ready().then(() => window.ymaps);
  }
  const existing = document.querySelector('script[data-mira-ymaps="1"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => window.ymaps.ready().then(() => resolve(window.ymaps)));
      existing.addEventListener('error', () => reject(new Error('Yandex Maps script failed')));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.dataset.miraYmaps = '1';
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
    script.async = true;
    script.onload = () => window.ymaps.ready().then(() => resolve(window.ymaps));
    script.onerror = () => reject(new Error('Yandex Maps script failed'));
    document.head.appendChild(script);
  });
}

export default function MapPage({ activeTab = 'map', onTabChange }) {
  const active = activeTab === 'map';
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const objectManagerRef = useRef(null);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [filling, setFilling] = useState(false);
  const [fillNotice, setFillNotice] = useState('');

  const load = useCallback(async ({ fill = 0 } = {}) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiFetch(`/api/map/devices?fill=${fill}`);
      const data = await readJsonResponse(response, 'Map devices');
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить карту');
      setPayload(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cache-only load: never burn Yandex quota on every page open.
  useEffect(() => {
    load({ fill: 0 });
  }, [load]);

  const points = useMemo(
    () => (payload?.devices || []).filter((item) => item.lat != null && item.lon != null),
    [payload],
  );

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    let cancelled = false;
    async function mountMap() {
      if (!payload?.yandex_maps_api_key || !mapNodeRef.current) {
        return;
      }
      try {
        const ymaps = await loadYmaps(payload.yandex_maps_api_key);
        if (cancelled || !mapNodeRef.current) {
          return;
        }
        if (!mapRef.current) {
          mapRef.current = new ymaps.Map(mapNodeRef.current, {
            center: MOSCOW,
            zoom: 10,
            controls: ['zoomControl', 'fullscreenControl', 'typeSelector'],
          });
          // ObjectManager: JSON features + viewport overlays (official large-set path).
          objectManagerRef.current = new ymaps.ObjectManager({
            clusterize: true,
            gridSize: 64,
            clusterDisableClickZoom: false,
          });
          objectManagerRef.current.objects.options.set('preset', 'islands#blueCircleDotIcon');
          objectManagerRef.current.clusters.options.set(
            'preset',
            'islands#invertedBlueClusterIcons',
          );
          mapRef.current.geoObjects.add(objectManagerRef.current);
        }

        objectManagerRef.current.removeAll();
        objectManagerRef.current.add({
          type: 'FeatureCollection',
          features: points.map((item, index) => ({
            type: 'Feature',
            id: item.device_id ?? item.serial ?? index,
            geometry: {
              type: 'Point',
              coordinates: [item.lat, item.lon],
            },
            properties: {
              balloonContentHeader: item.serial,
              balloonContentBody: [item.city, item.address, item.apartment ? `кв. ${item.apartment}` : '']
                .filter(Boolean)
                .join('<br/>'),
              hintContent: item.serial,
              clusterCaption: item.serial,
            },
          })),
        });
        if (points.length > 0) {
          mapRef.current.setBounds(objectManagerRef.current.getBounds(), {
            checkZoomRange: true,
            zoomMargin: 40,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Карта не загрузилась');
        }
      }
    }
    mountMap();
    return () => {
      cancelled = true;
      try {
        mapRef.current?.destroy?.();
      } catch {
        // ignore
      }
      mapRef.current = null;
      objectManagerRef.current = null;
    };
  }, [active, payload?.yandex_maps_api_key, points]);

  async function fillMore() {
    setFilling(true);
    setFillNotice('');
    setError('');
    try {
      // Only pending unique addresses; ok/failed stay in DB and are not re-sent.
      const response = await apiFetch('/api/map/geocode/fill', {
        method: 'POST',
        body: JSON.stringify({ limit: 25 }),
      });
      const data = await readJsonResponse(response, 'Map geocode fill');
      if (data.status === 'limit_exceeded' || data.status === 'budget_exhausted') {
        setFillNotice(data.message || 'Лимит геокодера на сегодня исчерпан. Попробуйте завтра.');
      } else if (!response.ok && data.status !== 'ok') {
        setError(data.message || data.reason || `HTTP ${response.status}`);
      } else {
        setFillNotice(data.message || `Геокодировано: ${data.ok || 0}`);
      }
      await load({ fill: 0 });
    } catch (err) {
      setError(err.message || 'Не удалось догеокодировать');
    } finally {
      setFilling(false);
    }
  }

  const totals = payload?.totals;
  const cache = payload?.cache;
  const budget = payload?.budget;

  return (
    <section className="space-y-3">
      <RisksTopBar
        actions={
          <>
            <button
              type="button"
              disabled={isLoading || filling || !payload?.api_key_configured}
              onClick={fillMore}
              className="min-h-10 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-800 shadow-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 sm:h-9"
            >
              {filling ? 'Геокодим…' : 'Догеокодировать'}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => load({ fill: 0 })}
              className="min-h-10 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-800 shadow-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 sm:h-9"
            >
              {isLoading ? 'Обновляем…' : 'Обновить'}
            </button>
          </>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-800 dark:bg-slate-950/70">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600 dark:text-slate-300">
          <span>
            На карте{' '}
            <strong className="tabular-nums text-slate-900 dark:text-white">
              {totals?.with_coords ?? '—'}
            </strong>
            {' / '}
            <span className="tabular-nums">{totals?.devices ?? '—'}</span>
          </span>
          {cache ? (
            <span className="text-slate-400">
              кэш: ok {cache.ok || 0} · pending {cache.pending || 0} · fail {cache.failed || 0}
            </span>
          ) : null}
          {budget ? (
            <span className="text-slate-400">
              бюджет геокода сегодня: {budget.used_today}/{budget.daily_limit}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Координаты кэшируются по адресу: повторно в Яндекс уходят только новые pending.
        </p>
        {payload && payload.api_key_configured === false ? (
          <p className="mt-2 text-amber-700 dark:text-amber-300">
            Задайте `YANDEX_MAPS_API_KEY` на бэкенде — без ключа геокод и карта не работают.
          </p>
        ) : null}
        {payload?.ip_geolocation?.available === false ? (
          <p className="mt-1 text-xs text-slate-400">
            IP из CoAP не используем: в БД не сохраняется, у NB это CGNAT оператора.
          </p>
        ) : null}
        {fillNotice ? (
          <p className="mt-2 text-amber-700 dark:text-amber-300">{fillNotice}</p>
        ) : null}
        {error ? <p className="mt-2 text-rose-600 dark:text-rose-400">{error}</p> : null}
      </div>

      <div
        ref={mapNodeRef}
        className="h-[70vh] min-h-[28rem] w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900"
      />
    </section>
  );
}
